import React, { FC, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import StableResponsiveContainer from '../general/stable-responsive-container/stable-responsive-container.component';
import { StatsResponse } from '../../models/stats.models';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import {
  formatSeasonDurationHours,
  getSportLayerColor,
  getSportTypeColor,
} from '../../pages/training-log/training-log.helpers';
import { buildBarSegments } from '../period-summary/period-summary.helpers';
import {
  buildFocusPanelItems,
  buildPeriodSportTypeChartData,
  buildSportTypeLegendItems,
  FocusPanelItem,
  formatDistributionPercent,
  formatSportChartValue,
  formatSportTypeLabel,
  formatYAxisTick,
  getSportsWithTypeData,
  getSportTypeKeys,
  getStackedSportTypeYDomain,
  hasSportTypeData,
  MetricMode,
  SportTypeLegendItem,
} from './sport-type-breakdown.helpers';
import { StackedSportChartRow } from '../sport-breakdown/sport-breakdown.helpers';
import './sport-type-breakdown.component.css';

interface SportTypeBreakdownProps {
  stats: StatsResponse | null;
  loading: boolean;
}

interface MetricToggleProps {
  value: MetricMode;
  onChange: (mode: MetricMode) => void;
}

const MetricToggle: FC<MetricToggleProps> = ({ value, onChange }) => (
  <div className="stb-toggle">
    <button type="button" className={value === 'duration' ? 'active' : ''} onClick={() => onChange('duration')}>
      Duration
    </button>
    <button type="button" className={value === 'distance' ? 'active' : ''} onClick={() => onChange('distance')}>
      Distance
    </button>
  </div>
);

interface SportTypeLegendProps {
  items: SportTypeLegendItem[];
}

const SportTypeLegend: FC<SportTypeLegendProps> = ({ items }) => (
  <div className="stb-legend">
    {items.map((item) => (
      <div className="stb-legend-item" key={item.key}>
        <div className="stb-legend-dot" style={{ background: item.color }} />
        {item.label} · {item.formattedTotal}
      </div>
    ))}
  </div>
);

interface SportTypeStackedBarChartProps {
  data: StackedSportChartRow[];
  sportTypeKeys: string[];
  sportKey: string;
  sportIndex: number;
  mode: MetricMode;
  distanceUnit: string;
}

const SportTypeStackedBarChart: FC<SportTypeStackedBarChartProps> = ({
  data,
  sportTypeKeys,
  sportKey,
  sportIndex,
  mode,
  distanceUnit,
}) => {
  const yDomain = useMemo(() => getStackedSportTypeYDomain(data, sportTypeKeys, mode), [data, sportTypeKeys, mode]);

  if (data.length === 0 || sportTypeKeys.length === 0) {
    return <div className="stb-chart-empty">No data</div>;
  }

  return (
    <div className="stb-chart-container" style={{ height: 200 }}>
      <StableResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="12%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            interval={0}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 10, fill: '#6B7280' }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value) => formatYAxisTick(value, mode, distanceUnit)}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) {
                return null;
              }

              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#111827' }}>{label}</div>
                  {payload
                    .filter((entry) => Number(entry.value) > 0)
                    .map((entry) => {
                      const typeKey = String(entry.dataKey);
                      const typeLabel = formatSportTypeLabel(typeKey);
                      return (
                        <div key={typeKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: entry.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: '#6B7280', minWidth: 24 }}>{typeLabel}</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>
                            {formatSportChartValue(Number(entry.value), mode, distanceUnit)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              );
            }}
          />
          {sportTypeKeys.map((typeKey, typeIndex) => (
            <Bar
              key={typeKey}
              dataKey={typeKey}
              stackId={`sport-types-${sportKey}`}
              fill={getSportTypeColor(sportKey, sportIndex, typeIndex, sportTypeKeys.length)}
              isAnimationActive={false}
              radius={typeIndex === sportTypeKeys.length - 1 ? [2, 2, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </StableResponsiveContainer>
    </div>
  );
};

interface SportFocusPanelProps {
  items: FocusPanelItem[];
  distanceUnit: string;
}

const formatFocusDistance = (distance: number, distanceUnit: string): string => {
  if (!distance) {
    return '—';
  }
  return `${distance % 1 === 0 ? distance : distance.toFixed(0)}${distanceUnit}`;
};

const formatSessionsLabel = (sessions: number): string => {
  return `${sessions} session${sessions === 1 ? '' : 's'}`;
};

const SportFocusPanel: FC<SportFocusPanelProps> = ({ items, distanceUnit }) => {
  const barSegments = useMemo(
    () =>
      buildBarSegments(
        items.map((item) => item.durationSeconds),
        items.map((item) => item.color)
      ),
    [items]
  );

  if (items.length === 0) {
    return <div className="stb-focus-empty">No focus data available</div>;
  }

  return (
    <div className="stb-focus-panel">
      <div className="stb-focus-grid">
        {items.map((item) => (
          <div
            key={item.key}
            className="stb-focus-item"
            style={{
              background: `${item.color}18`,
              borderLeftColor: item.color,
            }}
          >
            <div className="stb-focus-label" style={{ color: item.color }}>
              {item.label}
            </div>
            <div className="stb-focus-pct">{formatDistributionPercent(item.percent)}</div>
            <div className="stb-focus-detail">
              {formatSeasonDurationHours(item.durationSeconds).replace(/h$/, ' hrs')} ·{' '}
              {formatFocusDistance(item.distance, distanceUnit)}
            </div>
            <div className="stb-focus-detail">{formatSessionsLabel(item.sessions)}</div>
          </div>
        ))}
      </div>
      {barSegments.length > 0 && (
        <div className="stb-focus-bar">
          {barSegments.map((segment, index) => (
            <span
              key={index}
              style={{ background: segment.color, flex: segment.flex }}
              title={`${items[index]?.label}: ${formatDistributionPercent(items[index]?.percent ?? 0)}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface SportQuadrantProps {
  sportKey: string;
  sportIndex: number;
  stats: StatsResponse;
  mode: MetricMode;
  onModeChange: (mode: MetricMode) => void;
  distanceUnit: string;
  borderRight: boolean;
  borderBottom: boolean;
}

const SportQuadrant: FC<SportQuadrantProps> = ({
  sportKey,
  sportIndex,
  stats,
  mode,
  onModeChange,
  distanceUnit,
  borderRight,
  borderBottom,
}) => {
  const sportTypeKeys = useMemo(
    () => getSportTypeKeys(sportKey, stats.periodBreakdown ?? [], stats.sportBreakdown ?? {}),
    [sportKey, stats.periodBreakdown, stats.sportBreakdown]
  );

  const periodData = useMemo(
    () => buildPeriodSportTypeChartData(stats.periodBreakdown ?? [], sportKey, sportTypeKeys, mode),
    [stats.periodBreakdown, sportKey, sportTypeKeys, mode]
  );

  const legendItems = useMemo(
    () => buildSportTypeLegendItems(sportKey, sportIndex, stats.sportBreakdown ?? {}, sportTypeKeys, mode),
    [sportKey, sportIndex, stats.sportBreakdown, sportTypeKeys, mode]
  );

  const focusItems = useMemo(
    () => buildFocusPanelItems(stats.sportBreakdown?.[sportKey]?.focus),
    [stats.sportBreakdown, sportKey]
  );

  const subtitle = mode === 'duration' ? 'Hours by type per period' : `Distance by type per period (${distanceUnit})`;

  const borderClasses = [
    borderRight ? 'stb-quadrant--border-right' : '',
    borderBottom ? 'stb-quadrant--border-bottom' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`stb-quadrant ${borderClasses}`}>
      <div className="stb-quadrant-header">
        <div>
          <div className="stb-quadrant-title" style={{ color: getSportLayerColor(sportKey, sportIndex) }}>
            {sportKey}
          </div>
          <div className="stb-quadrant-subtitle">{subtitle}</div>
        </div>
        <MetricToggle value={mode} onChange={onModeChange} />
      </div>

      {legendItems.length > 0 && <SportTypeLegend items={legendItems} />}
      <SportTypeStackedBarChart
        data={periodData}
        sportTypeKeys={sportTypeKeys}
        sportKey={sportKey}
        sportIndex={sportIndex}
        mode={mode}
        distanceUnit={distanceUnit}
      />
      <SportFocusPanel items={focusItems} distanceUnit={distanceUnit} />
    </div>
  );
};

const SportTypeBreakdown: FC<SportTypeBreakdownProps> = ({ stats, loading }) => {
  const [metricBySport, setMetricBySport] = useState<Record<string, MetricMode>>({});
  const distanceUnit = getMeasurementUnit();

  const sportKeys = useMemo(() => getSportsWithTypeData(stats), [stats]);

  const getMetric = (sportKey: string): MetricMode => metricBySport[sportKey] ?? 'duration';

  const setMetric = (sportKey: string, mode: MetricMode) => {
    setMetricBySport((prev) => ({ ...prev, [sportKey]: mode }));
  };

  const rowCount = Math.ceil(sportKeys.length / 2);

  if (loading) {
    return (
      <div className="stb-card">
        <div className="stb-header">
          <div className="stb-title">Sport Type Breakdown</div>
          <div className="stb-subtitle">How each sport splits across its sub-types over the season</div>
        </div>
        <div className="stb-loading">
          <div className="stb-skeleton" />
          <div className="stb-skeleton" />
        </div>
      </div>
    );
  }

  if (!hasSportTypeData(stats)) {
    return (
      <div className="stb-card">
        <div className="stb-header">
          <div className="stb-title">Sport Type Breakdown</div>
          <div className="stb-subtitle">How each sport splits across its sub-types over the season</div>
        </div>
        <div className="stb-empty">No sport type data for this season</div>
      </div>
    );
  }

  return (
    <div className="stb-card">
      <div className="stb-header">
        <div className="stb-title">Sport Type Breakdown</div>
        <div className="stb-subtitle">How each sport splits across its sub-types over the season</div>
      </div>

      <div className="stb-grid">
        {stats &&
          sportKeys.map((sportKey, index) => {
            const col = index % 2;
            const row = Math.floor(index / 2);
            const borderRight = col === 0;
            const borderBottom = row < rowCount - 1;

            return (
              <SportQuadrant
                key={sportKey}
                sportKey={sportKey}
                sportIndex={index}
                stats={stats}
                mode={getMetric(sportKey)}
                onModeChange={(mode) => setMetric(sportKey, mode)}
                distanceUnit={distanceUnit}
                borderRight={borderRight}
                borderBottom={borderBottom}
              />
            );
          })}
      </div>
    </div>
  );
};

export default SportTypeBreakdown;
